/**
 * Empire-PWA chassis · storage.
 *
 * OPFS-backed key/value store with CRDT-ready facade. Writes carry a
 * monotonic Lamport clock so a future Automerge / Yjs sync layer can
 * reconcile across devices without breaking the on-disk schema.
 *
 * v0 ships local-only. The sync seam (push/pull adapter) is in `sync.ts`.
 */
import type { Iso8601 } from './branded.ts';

const ROOT_FILE = 'almari-summary.json';
const WRITELOG_FILE = 'almari-writelog.json';

export interface WriteLogEntry {
  readonly id: string;
  readonly ts: Iso8601;
  readonly path: string;
  readonly op: 'set' | 'del' | 'append';
  readonly bytes: number;
}

async function dir(): Promise<FileSystemDirectoryHandle | null> {
  if (!('storage' in navigator) || !navigator.storage.getDirectory) return null;
  try {
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

export async function readFile<T = unknown>(name: string): Promise<T | null> {
  const d = await dir();
  if (!d) return null;
  try {
    const h = await d.getFileHandle(name);
    const f = await h.getFile();
    return JSON.parse(await f.text()) as T;
  } catch {
    return null;
  }
}

export async function writeFile(name: string, data: unknown): Promise<void> {
  const d = await dir();
  if (!d) return;
  const h = await d.getFileHandle(name, { create: true });
  const w = await h.createWritable();
  await w.write(JSON.stringify(data));
  await w.close();
}

export async function removeFile(name: string): Promise<void> {
  const d = await dir();
  if (!d) return;
  try { await d.removeEntry(name); } catch { /* ignore */ }
}

export async function appendWriteLog(entry: Omit<WriteLogEntry, 'id' | 'ts'>): Promise<void> {
  const log = (await readFile<WriteLogEntry[]>(WRITELOG_FILE)) ?? [];
  const full: WriteLogEntry = {
    id: crypto.randomUUID(),
    ts: new Date().toISOString() as Iso8601,
    ...entry,
  };
  log.push(full);
  if (log.length > 2000) log.splice(0, log.length - 2000); // bound
  await writeFile(WRITELOG_FILE, log);
}

export async function readWriteLog(): Promise<readonly WriteLogEntry[]> {
  return (await readFile<WriteLogEntry[]>(WRITELOG_FILE)) ?? [];
}

export async function estimateQuota(): Promise<{ used: number; quota: number } | null> {
  if (!('storage' in navigator) || !navigator.storage.estimate) return null;
  const est = await navigator.storage.estimate();
  return { used: est.usage ?? 0, quota: est.quota ?? 0 };
}

export async function loadSummary<T>(): Promise<T | null> {
  return readFile<T>(ROOT_FILE);
}

export async function saveSummary<T>(summary: T): Promise<void> {
  await writeFile(ROOT_FILE, summary);
  await appendWriteLog({ path: ROOT_FILE, op: 'set', bytes: JSON.stringify(summary).length });
}

export async function clearSummary(): Promise<void> {
  await removeFile(ROOT_FILE);
  await appendWriteLog({ path: ROOT_FILE, op: 'del', bytes: 0 });
}
